import { makeAutoObservable } from 'mobx'

import {
  CitizenshipAvia,
  CitizenshipBeket,
  CitizenshipTour,
} from 'shared/api/CitizenshipModel'
import { CitizenshipsApi } from 'shared/api/CitizenshipsApi'

export type CitizenshipLoadStatus = 'idle' | 'loading' | 'success' | 'error'

export class CitizenshipStore {
  citizenshipAvia: CitizenshipAvia[] | null = null
  citizenshipBeket: CitizenshipBeket[] | null = null
  citizenshipTour: CitizenshipTour[] | null = null
  aviaStatus: CitizenshipLoadStatus = 'idle'
  tourStatus: CitizenshipLoadStatus = 'idle'
  private aviaRequest: Promise<CitizenshipAvia[] | undefined> | null = null
  private tourRequest: Promise<CitizenshipTour[] | undefined> | null = null

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true })
  }

  onSetCitizenship(data: CitizenshipAvia[]) {
    this.citizenshipAvia = data
    this.aviaStatus = 'success'
  }

  onSetCitizenshipBeket(data: CitizenshipBeket[]) {
    this.citizenshipBeket = data
  }

  onSetCitizenshipTour(data: CitizenshipTour[]) {
    this.citizenshipTour = data
    this.tourStatus = 'success'
  }

  ensureAviaCitizenships(): Promise<CitizenshipAvia[] | undefined> {
    if (this.citizenshipAvia !== null) {
      this.aviaStatus = 'success'
      return Promise.resolve(this.citizenshipAvia)
    }

    if (this.aviaRequest) return this.aviaRequest

    this.aviaStatus = 'loading'
    this.aviaRequest = this.loadAviaCitizenships().finally(() => {
      this.aviaRequest = null
    })

    return this.aviaRequest
  }

  ensureTourCitizenships(): Promise<CitizenshipTour[] | undefined> {
    if (this.citizenshipTour !== null) {
      this.tourStatus = 'success'
      return Promise.resolve(this.citizenshipTour)
    }

    if (this.tourRequest) return this.tourRequest

    this.tourStatus = 'loading'
    this.tourRequest = this.loadTourCitizenships().finally(() => {
      this.tourRequest = null
    })

    return this.tourRequest
  }

  private async loadAviaCitizenships(): Promise<CitizenshipAvia[] | undefined> {
    try {
      const { data } = await CitizenshipsApi.getCitizenships()

      this.onSetCitizenship(data.data)
      return data.data
    } catch {
      this.aviaStatus = 'error'
      return undefined
    }
  }

  private async loadTourCitizenships(): Promise<CitizenshipTour[] | undefined> {
    try {
      const { data } = await CitizenshipsApi.getCitizenshipTour()

      this.onSetCitizenshipTour(data.data)
      return data.data
    } catch {
      this.tourStatus = 'error'
      return undefined
    }
  }
}

export default new CitizenshipStore()
